import {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
    type FC,
    type ReactNode,
    type Ref
} from "react";

import { css, keyframes } from "@compiled/react";
// @ts-ignore
import { useInView } from "react-intersection-observer";

import { isReducedMotion } from "@atlaskit/motion";
import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { Image } from "@/libraries/design/components";
import { ColumnLayout, wacToken } from "@/libraries/design/foundations";
import { DataEventProps } from "@/libraries/wpl/foundations/src/data-event-props";

import { ValuePropCard, ValuePropCardData } from "./value-prop-card";

export type MediaAspectRatio = "1 / 1" | "16 / 9";

const DEFAULT_CYCLE_DURATION_MS = 6000;

export interface ValuePropsProps {
    title: string;
    subtitle?: string;
    cards: ValuePropCardData[];
    mediaAspectRatio?: MediaAspectRatio;
    dataEventProps?: DataEventProps;
    cycleDurationMs?: number;
}

const containerCss = css({
    paddingBlock: "80px",
    display: "flex",
    flexDirection: "column",
    gap: token("space.800"),
    [media.above.lg]: {
        paddingBlock: "96px"
    },
    [media.below.sm]: {
        paddingBlock: token("space.800"),
        gap: token("space.400")
    }
});

const titleContainerCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.100"),
    [media.only.sm]: {
        gridColumn: "1 / -2"
    }
});

const zeroMarginCss = css({
    margin: 0
});

const mobileSectionCss = css({
    display: "block",
    [media.above.sm]: {
        display: "none"
    }
});

const desktopSectionCss = css({
    display: "none",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles
    [media.above.md]: { display: "block" }
});

const nonDesktopSectionCss = css({
    display: "none",
    [media.above.sm]: { display: "block" },
    [media.above.md]: { display: "none" }
});

const mediaCss = css({
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 0,
    [media.below.sm]: {
        width: "100%"
    }
});

const standaloneMediaCss = css({
    [media.below.sm]: {
        display: "none"
    }
});

const cardsRowCss = css({
    display: "flex",
    alignItems: "stretch",
    gap: token("space.200"),
    [media.below.sm]: {
        gap: 0
    }
});

const cardListCss = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.200"),
    flex: 1,
    listStyle: "none",
    margin: 0,
    padding: 0,
    [media.above.lg]: {
        gap: token("space.400"),
        marginInlineEnd: "60px"
    }
});

const mediaFillCss = css({
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block"
});

const mediaAspectRatio1x1Css = css({ aspectRatio: "1 / 1" });
const mediaAspectRatio16x9Css = css({ aspectRatio: "16 / 9" });

const mediaContainerCss = css({
    position: "relative",
    width: "100%",
    overflow: "hidden",
    borderRadius: wacToken("corner.radius.card")
});

const mobileInlineMediaCss = css({
    "display": "none",
    [media.below.sm]: {
        display: "block",
        marginBlockStart: token("space.150"),
        width: "100%"
    },
    "@media (prefers-reduced-motion: no-preference)": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        animationName: keyframes({ from: { opacity: 0 }, to: { opacity: 1 } }),
        animationDuration: wacToken("motion.duration.400ms"),
        animationTimingFunction: wacToken("motion.easing.ease40-in-out"),
        animationFillMode: "both"
    }
});

const mediaLayerCss = css({
    "position": "absolute",
    "inset": 0,
    "width": "100%",
    "height": "100%",
    "@media (prefers-reduced-motion: no-preference)": {
        transition: `opacity ${wacToken("motion.duration.400ms")} ${wacToken(
            "motion.easing.ease40-in-out"
        )}`
    }
});

const mediaLayerActiveCss = css({ opacity: 1 });

const mediaLayerInactiveCss = css({
    opacity: 0,
    pointerEvents: "none"
});

const fadeUpDelay01Css = css({ animationDelay: "0.1s" });
const fadeUpDelay02Css = css({ animationDelay: "0.2s" });
const fadeUpDelay03Css = css({ animationDelay: "0.3s" });

const mobileCardListWrapperCss = css({
    position: "relative"
});

const mobileBottomFadeCss = css({
    "position": "absolute",
    "insetInlineStart": 0,
    "insetInlineEnd": 0,
    "insetBlockEnd": 0,
    "height": "72px",
    "zIndex": 2,
    // eslint-disable-next-line local/ensure-wac-token-usage
    "background": "linear-gradient(to bottom, transparent, white)",
    "pointerEvents": "none",
    // Only shown below sm breakpoint
    [media.above.sm]: {
        display: "none"
    },
    "@media (prefers-reduced-motion: no-preference)": {
        transition: `opacity 400ms ${wacToken("motion.easing.ease40-in-out")}`
    }
});

const mobileBottomFadeVisibleCss = css({ opacity: 1 });
const mobileBottomFadeHiddenCss = css({ opacity: 0 });

const fadeUpCss = css({
    "opacity": 0,
    "@media (prefers-reduced-motion: no-preference)": {
        animationName: keyframes({
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            from: { opacity: 0, transform: "translateY(20px)" },
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            to: { opacity: 1, transform: "translateY(0)" }
        }),
        animationDuration: "1s",
        animationTimingFunction: "ease",
        animationFillMode: "forwards",
        // Paused until IO fires — prevents animation completing off-screen
        animationPlayState: "paused"
    },
    "@media (prefers-reduced-motion: reduce)": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        animationName: keyframes({ from: { opacity: 0 }, to: { opacity: 1 } }),
        animationDuration: wacToken("motion.duration.200ms"),
        animationTimingFunction: "linear",
        animationFillMode: "forwards",
        animationPlayState: "paused"
    }
});

// Applied when IO fires — unpauses the fade-up animation
const fadeUpActiveCss = css({
    "@media (prefers-reduced-motion: no-preference)": {
        animationPlayState: "running"
    },
    "@media (prefers-reduced-motion: reduce)": {
        animationPlayState: "running"
    }
});

const VideoContent: FC<{
    card: ValuePropCardData;
    isActive: boolean;
    videoType: string;
    onDuration?: (ms: number) => void;
    onPlayingChange?: (playing: boolean) => void;
}> = ({ card, isActive, videoType, onDuration, onPlayingChange }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    // Reset to start and optimistically signal playing when card becomes active.
    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        if (!isActive) {
            onPlayingChange?.(false);
            return;
        }

        video.currentTime = 0;
        if (
            video.readyState >= 1 &&
            video.duration &&
            isFinite(video.duration)
        ) {
            onDuration?.(Math.round(video.duration * 1000));
        }
        onPlayingChange?.(true);
        video.play().catch(() => {
            onPlayingChange?.(false);
        });
    }, [isActive, onDuration, onPlayingChange]);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const handleMetadata = () => {
            if (video.duration && isFinite(video.duration)) {
                onDuration?.(Math.round(video.duration * 1000));
            }
        };
        const handlePlaying = () => onPlayingChange?.(true);
        const handleNotPlaying = () => onPlayingChange?.(false);

        if (video.readyState >= 1) handleMetadata();
        video.addEventListener("loadedmetadata", handleMetadata);
        video.addEventListener("playing", handlePlaying);
        video.addEventListener("pause", handleNotPlaying);
        video.addEventListener("waiting", handleNotPlaying);

        return () => {
            video.removeEventListener("loadedmetadata", handleMetadata);
            video.removeEventListener("playing", handlePlaying);
            video.removeEventListener("pause", handleNotPlaying);
            video.removeEventListener("waiting", handleNotPlaying);
        };
    }, [onDuration, onPlayingChange]);

    return (
        <video
            ref={videoRef}
            data-value-props-video
            autoPlay
            muted
            loop
            playsInline
            aria-label={isActive ? card.mediaAlt || card.title : undefined}
            aria-hidden={!isActive}
            css={mediaFillCss}
        >
            <source src={card.videoSrc} type={`video/${videoType}`} />
        </video>
    );
};

const MediaContent = ({
    card,
    isActive = true,
    onDuration,
    onPlayingChange
}: {
    card: ValuePropCardData;
    isActive?: boolean;
    onDuration?: (ms: number) => void;
    onPlayingChange?: (playing: boolean) => void;
}) => {
    if (card.imageSrc) {
        return (
            <Image
                src={card.imageSrc}
                alt={isActive ? card.mediaAlt || card.title : ""}
                aria-hidden={!isActive}
                css={mediaFillCss}
            />
        );
    }

    if (card.videoSrc) {
        const videoFormat = card.videoSrc.split(".").at(-1);
        const videoType = videoFormat === "webm" ? "webm" : "mp4";

        return (
            <VideoContent
                card={card}
                isActive={isActive}
                videoType={videoType}
                onDuration={onDuration}
                onPlayingChange={onPlayingChange}
            />
        );
    }

    return null;
};

interface MediaStackProps {
    cards: ValuePropCardData[];
    activeIndex: number;
    mediaAspectRatio: MediaAspectRatio;
    onDuration?: (ms: number) => void;
    onPlayingChange?: (playing: boolean) => void;
}

const MediaStack = memo(
    ({
        cards,
        activeIndex,
        mediaAspectRatio,
        onDuration,
        onPlayingChange
    }: MediaStackProps) => (
        <div
            css={[
                mediaContainerCss,
                mediaAspectRatio === "16 / 9" && mediaAspectRatio16x9Css,
                mediaAspectRatio === "1 / 1" && mediaAspectRatio1x1Css
            ]}
        >
            {cards.map((card, index) => {
                const isCardActive = index === activeIndex;
                return (
                    <div
                        key={index}
                        css={[
                            mediaLayerCss,
                            isCardActive && mediaLayerActiveCss,
                            !isCardActive && mediaLayerInactiveCss
                        ]}
                        aria-hidden={!isCardActive}
                    >
                        <MediaContent
                            card={card}
                            isActive={isCardActive}
                            onDuration={isCardActive ? onDuration : undefined}
                            onPlayingChange={
                                isCardActive ? onPlayingChange : undefined
                            }
                        />
                    </div>
                );
            })}
        </div>
    )
);

interface CardListItemProps {
    card: ValuePropCardData;
    index: number;
    activeIndex: number;
    dataEventProps?: DataEventProps;
    isPlaying: boolean;
    cycleDurationMs: number;
    activeVideoDurationMs: number | null;
    isActiveVideoPlaying: boolean;
    onCardClick: (index: number) => void;
    onAdvance: () => void;
    onPlayPauseToggle: () => void;
    mediaTile: ReactNode;
}

const CardListItem = memo(
    ({
        card,
        index,
        activeIndex,
        dataEventProps,
        isPlaying,
        cycleDurationMs,
        activeVideoDurationMs,
        isActiveVideoPlaying,
        onCardClick,
        onAdvance,
        onPlayPauseToggle,
        mediaTile
    }: CardListItemProps) => {
        const isActive = index === activeIndex;
        const onActivate = useCallback(
            () => onCardClick(index),
            [onCardClick, index]
        );

        return (
            <li>
                <ValuePropCard
                    card={card}
                    isActive={isActive}
                    index={index}
                    dataEventProps={dataEventProps}
                    onActivate={onActivate}
                    isPlaying={isPlaying}
                    onAdvance={onAdvance}
                    cycleDurationMs={cycleDurationMs}
                    activeVideoDurationMs={
                        isActive ? activeVideoDurationMs : null
                    }
                    isActiveVideoPlaying={
                        isActive ? isActiveVideoPlaying : false
                    }
                    onPlayPauseToggle={onPlayPauseToggle}
                />
                {isActive && <div css={mobileInlineMediaCss}>{mediaTile}</div>}
            </li>
        );
    }
);

interface CardListSectionProps {
    cards: ValuePropCardData[];
    activeIndex: number;
    dataEventProps?: DataEventProps;
    isPlaying: boolean;
    cycleDurationMs: number;
    activeVideoDurationMs: number | null;
    isActiveVideoPlaying: boolean;
    onCardClick: (index: number) => void;
    onAdvance: () => void;
    onPlayPauseToggle: () => void;
    mediaTile: ReactNode;
    ulRef?: Ref<HTMLUListElement>;
    minHeight?: number;
    showMobileFade?: boolean;
}

const CardListSection = memo(
    ({
        cards,
        activeIndex,
        dataEventProps,
        isPlaying,
        cycleDurationMs,
        activeVideoDurationMs,
        isActiveVideoPlaying,
        onCardClick,
        onAdvance,
        onPlayPauseToggle,
        mediaTile,
        ulRef,
        minHeight,
        showMobileFade
    }: CardListSectionProps) => (
        <div
            css={[
                cardsRowCss,
                showMobileFade !== undefined && mobileCardListWrapperCss
            ]}
        >
            <ul
                css={cardListCss}
                ref={ulRef}
                style={minHeight !== undefined ? { minHeight } : undefined}
            >
                {cards.map((card, index) => (
                    <CardListItem
                        key={index}
                        card={card}
                        index={index}
                        activeIndex={activeIndex}
                        dataEventProps={dataEventProps}
                        isPlaying={isPlaying}
                        cycleDurationMs={cycleDurationMs}
                        activeVideoDurationMs={activeVideoDurationMs}
                        isActiveVideoPlaying={isActiveVideoPlaying}
                        onCardClick={onCardClick}
                        onAdvance={onAdvance}
                        onPlayPauseToggle={onPlayPauseToggle}
                        mediaTile={mediaTile}
                    />
                ))}
            </ul>
            {showMobileFade !== undefined && (
                <div
                    css={[
                        mobileBottomFadeCss,
                        showMobileFade
                            ? mobileBottomFadeVisibleCss
                            : mobileBottomFadeHiddenCss
                    ]}
                    aria-hidden={true}
                    data-testid="value-props-mobile-bottom-fade"
                />
            )}
        </div>
    )
);

const ValueProps: FC<ValuePropsProps> = ({
    title,
    subtitle,
    cards,
    mediaAspectRatio = "1 / 1",
    dataEventProps,
    cycleDurationMs = DEFAULT_CYCLE_DURATION_MS
}) => {
    // ── State ────────────────────────────────────────────────────────────────
    const [activeIndex, setActiveIndex] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [activeVideoDurationMs, setActiveVideoDurationMs] = useState<
        number | null
    >(null);
    const [isActiveVideoPlaying, setIsActiveVideoPlaying] = useState(false);
    const [cardListMinHeight, setCardListMinHeight] = useState<
        number | undefined
    >(undefined);
    const [tabletCardListMinHeight, setTabletCardListMinHeight] = useState<
        number | undefined
    >(undefined);
    const [fadeVisible, setFadeVisible] = useState(false);
    const [hasEnteredView, setHasEnteredView] = useState(false);

    // ── Refs ─────────────────────────────────────────────────────────────────
    const containerRef = useRef<HTMLDivElement | null>(null);
    const cardListRef = useRef<HTMLUListElement | null>(null);
    const tabletCardListRef = useRef<HTMLUListElement | null>(null);
    const { ref: ioRef, inView } = useInView({ threshold: 0.35 });
    const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // ── Effects ──────────────────────────────────────────────────────────────
    useEffect(() => {
        if (inView) {
            setActiveIndex(0);
            setFadeVisible(false);
            setIsPlaying(!isReducedMotion());
            setHasEnteredView(true);
        } else {
            setIsPlaying(false);
        }
    }, [inView]);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const videos = container.querySelectorAll<HTMLVideoElement>(
            "video[data-value-props-video]"
        );
        videos.forEach((video) => {
            if (isPlaying) {
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                void video.play().catch(() => {});
            } else {
                video.pause();
            }
        });
    }, [isPlaying, activeIndex]);

    useEffect(() => {
        setActiveVideoDurationMs(null);
    }, [activeIndex]);

    // Track each section's card list max height and lock it as min-height
    // so the column never shrinks when an active card collapses.
    useEffect(() => {
        const observers: ResizeObserver[] = [];
        const rafIds: number[] = [];

        const watch = (
            el: HTMLUListElement | null,
            setter: (h: number) => void
        ) => {
            if (!el) return;
            let rafId = 0;
            const observer = new ResizeObserver(() => {
                cancelAnimationFrame(rafId);
                rafId = requestAnimationFrame(() => {
                    const h = el.getBoundingClientRect().height;
                    setter(h);
                });
                rafIds.push(rafId);
            });
            observer.observe(el);
            observers.push(observer);
        };

        watch(cardListRef.current, (h) =>
            setCardListMinHeight((prev) =>
                prev === undefined || h > prev ? h : prev
            )
        );
        watch(tabletCardListRef.current, (h) =>
            setTabletCardListMinHeight((prev) =>
                prev === undefined || h > prev ? h : prev
            )
        );
        return () => {
            observers.forEach((o) => o.disconnect());
            rafIds.forEach((id) => cancelAnimationFrame(id));
        };
    }, []);

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleAdvance = useCallback(() => {
        setActiveIndex((prev) => (prev + 1) % cards.length);
    }, [cards.length]);

    const handleCardClick = useCallback(
        (index: number) => {
            if (index === activeIndex) return;
            setActiveIndex(index);
            setIsPlaying(!isReducedMotion());
            // Mobile bottom fade — manual select only, not auto-advance
            if (!isReducedMotion()) {
                if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
                setFadeVisible(true);
                fadeTimerRef.current = setTimeout(() => {
                    setFadeVisible(false);
                    fadeTimerRef.current = null;
                }, 1800);
            }
        },
        [activeIndex]
    );

    const handlePlayPauseToggle = useCallback(() => {
        setIsPlaying((prev) => !prev);
    }, []);

    const handleVideoDuration = useCallback((ms: number) => {
        setActiveVideoDurationMs(ms);
    }, []);

    const handleVideoPlayingChange = useCallback((playing: boolean) => {
        setIsActiveVideoPlaying(playing);
    }, []);

    const setContainerRef = useCallback(
        (node: HTMLDivElement | null) => {
            containerRef.current = node;
            ioRef(node);
        },
        [ioRef]
    );

    // ── Early return ─────────────────────────────────────────────────────────
    if (!cards || cards.length === 0) {
        return null;
    }

    const activeCard = cards[activeIndex];

    const mediaTile = (
        <MediaStack
            cards={cards}
            activeIndex={activeIndex}
            mediaAspectRatio={mediaAspectRatio}
            onDuration={handleVideoDuration}
            onPlayingChange={handleVideoPlayingChange}
        />
    );

    const mediaContent = activeCard && (
        <div css={[mediaCss, standaloneMediaCss]}>{mediaTile}</div>
    );

    const sharedCardListProps = {
        cards,
        activeIndex,
        dataEventProps,
        isPlaying,
        cycleDurationMs,
        activeVideoDurationMs,
        isActiveVideoPlaying,
        onCardClick: handleCardClick,
        onAdvance: handleAdvance,
        onPlayPauseToggle: handlePlayPauseToggle,
        mediaTile
    };

    return (
        <div ref={setContainerRef} css={containerCss} data-testid="value-props">
            <ColumnLayout layout="8-4">
                <div css={[zeroMarginCss, titleContainerCss]}>
                    <h2
                        css={[
                            zeroMarginCss,
                            fadeUpCss,
                            hasEnteredView && fadeUpActiveCss,
                            fadeUpDelay01Css
                        ]}
                    >
                        {title}
                    </h2>
                    {subtitle && (
                        <p
                            css={[
                                zeroMarginCss,
                                fadeUpCss,
                                hasEnteredView && fadeUpActiveCss,
                                fadeUpDelay02Css
                            ]}
                            className="body-lg"
                        >
                            {subtitle}
                        </p>
                    )}
                </div>
                <div aria-hidden="true" />
            </ColumnLayout>
            <div
                css={[
                    fadeUpCss,
                    hasEnteredView && fadeUpActiveCss,
                    subtitle ? fadeUpDelay03Css : fadeUpDelay02Css
                ]}
            >
                <div css={desktopSectionCss}>
                    <ColumnLayout layout="6-6" verticalAlignment="center">
                        <CardListSection
                            {...sharedCardListProps}
                            ulRef={cardListRef}
                            minHeight={cardListMinHeight}
                        />
                        {mediaContent}
                    </ColumnLayout>
                </div>
                <div css={nonDesktopSectionCss}>
                    <ColumnLayout layout="6-6" verticalAlignment="center">
                        <CardListSection
                            {...sharedCardListProps}
                            ulRef={tabletCardListRef}
                            minHeight={tabletCardListMinHeight}
                        />
                        {mediaContent}
                    </ColumnLayout>
                </div>
                <div css={mobileSectionCss}>
                    <ColumnLayout layout="6-6">
                        <CardListSection
                            {...sharedCardListProps}
                            showMobileFade={fadeVisible}
                        />
                        {mediaContent}
                    </ColumnLayout>
                </div>
            </div>
        </div>
    );
};

export default ValueProps;
